import { FC, useCallback, useEffect, useState } from 'react';
import MapBox, {
  GeoJSONSource,
  MapMouseEvent,
  MapTouchEvent,
} from 'react-map-gl';
import { useNavigate, useParams } from 'react-router-dom';
import { DrawCreateEvent } from '@mapbox/mapbox-gl-draw';
import cn from 'classnames';
import {
  MAP_ENTITY_PARAM_VALUES,
  predefinedTemplates,
} from 'constants/entities';
import {
  BOUNDARY_GRID_CELL_LAYER,
  DEFAULT_FLY_TO_SETTINGS,
  ELEVATION_STATIC_LAYER,
  FIRE_STATIC_LAYER,
  FRONTLINE_STATIC_LAYER,
  GEOCODER_POINT_MAIN,
  GEOCODER_POLYGON_MAIN_FILL,
  IMAGERY_BOUNDARY_LAYER,
  IMAGERY_PREVIEW_FILL_LAYER,
  LABEL_STATIC_LAYER,
  LAYERS_LINE_MAIN_FILL,
  LAYERS_POINT,
  LAYERS_POLYGON_MAIN_FILL,
  LAYERS_POLYGON_MAIN_LINE,
  LINE_STATIC_LAYER,
  LOGISTIC_STATIC_LAYER,
  MAP_BOX_TOKEN,
  OFFSET_TO_FIT_PREVIEW,
  POINT_STATIC_LAYER,
  POLYGON_STATIC_LAYER,
  SATELLITE_IMAGE_STATIC_LAYER,
  SATELLITE_STATIC_LAYER,
} from 'constants/map';
import { MAP_CREATE_MODE_PREFIX } from 'constants/mapControl';
import {
  CREATE_DRAW_FEATURE_MODAL,
  CREATE_QUICK_ENTITY_MODAL,
  ENTITY_DETAILS_MODAL,
  ENTITY_PREVIEW_MODAL,
  IMAGERY_UPLOAD_MODAL,
  MAP_TOOLS_INFO_POPUP,
} from 'constants/modals';
import { AstraCursors, AstraCursorTitle, mapRoutes } from 'constants/routes';
import { useAppDispatch, useAppSelector, useQuickEntityCreation } from 'hooks';
import { useMap, useMapRef } from 'hooks/map';
import { useDoubleTap } from 'hooks/useDoubleTap';
import { useLoadEntityTypeImages } from 'hooks/useLoadEntityTypeImages';
import { useMapDraw } from 'hooks/useMapDraw';
import {
  DrawCreateCustomEvent,
  DrawCustomFeature,
  DrawDragMoveCustomEvent,
  DrawModeChangeCustomEvent,
  IImageryPreview,
} from 'interfaces';
import { authSelector } from 'store/slices/auth/selectors';
import { contextMenuActions } from 'store/slices/mapV2/mapReducer/contextMenuSlice';
import { contextMenuSelector } from 'store/slices/mapV2/mapReducer/contextMenuSlice/selectors';
import { showCustomCursorsSelector } from 'store/slices/mapV2/mapReducer/settingsSlice/selectors';
import { boundaryGridActions } from 'store/slices/mapV2/mapReducer/toolsReducer/boundaryGridSlice';
import { boundaryGridMapSelector } from 'store/slices/mapV2/mapReducer/toolsReducer/boundaryGridSlice/selectors';
import { commonActions } from 'store/slices/mapV2/mapReducer/toolsReducer/commonSlice';
import { drawActions } from 'store/slices/mapV2/mapReducer/toolsReducer/drawSlice';
import { geocoderActions } from 'store/slices/mapV2/mapReducer/toolsReducer/geocoderSlice';
import { stateActions as toolsStateActions } from 'store/slices/mapV2/mapReducer/toolsReducer/stateSlice';
import { interactionModeSelector } from 'store/slices/mapV2/mapReducer/toolsReducer/stateSlice/selectors';
import { getImageryThunk } from 'store/slices/mapV2/tabsReducer/imagerySlice/actions';
import { createQuickMapEntityThunk } from 'store/slices/mapV2/tabsReducer/layersReducer/mapEntitiesSlice/actions';
import {
  entitiesSelector,
  predefinedTemplateSelector,
} from 'store/slices/mapV2/tabsReducer/layersReducer/mapEntitiesSlice/selectors';
import { stateActions as layersStateActions } from 'store/slices/mapV2/tabsReducer/layersReducer/stateSlice';
import { imagerySelector } from 'store/slices/mapV2/tabsReducer/selectors';
import { modalsActions } from 'store/slices/service/modalsSlice';
import { modalSelector } from 'store/slices/service/modalsSlice/selectors';
import {
  BoundaryGridCellGeoJSON,
  ContextMenu as IContextMenu,
  ContextMenuTypes,
  DrawEventActions,
  DrawFeatureCommonEvent,
  Entity,
  FieldsUpdateState,
  ImageryBoundaryGeoJSON,
  MapCommonModes,
  MapCustomDrawModes,
  MapDrawModes,
  MapEntity,
  MapEntityTypes,
  PointClusterGeoJSON,
} from 'types';
import { useDebouncedCallback } from 'use-debounce';

import { ContextMenu } from 'components/Map/ContextMenu';
import { CreateDrawFeatureModal } from 'components/Map/CreateDrawFeatureModal';
import { CreateQuickEntityModal } from 'components/Map/CreateQuickEntityModal';
import { EntityDetails } from 'components/Map/EntityDetails';
import { EntityPreview } from 'components/Map/EntityPreview';
import FeatureCollection from 'components/Map/FeatureCollection';
import { Legend } from 'components/Map/Legend';
import { MapDrawControl } from 'components/Map/MapToolsControl/MapDrawControl';
import { MapToolsInfoPopup } from 'components/Map/MapToolsInfoPopup';
import { AimSource } from 'components/Map/Sources/AimSource';
import { ArtillerySource } from 'components/Map/Sources/ArtillerySource';
import { BoundaryGridSource } from 'components/Map/Sources/BoundaryGridSource';
import { DemSource } from 'components/Map/Sources/DemSource';
import { ElevationSource } from 'components/Map/Sources/ElevationSource';
import { FireSource } from 'components/Map/Sources/FireSource';
import { GeocoderSource } from 'components/Map/Sources/GeocoderSource';
import { GeoconfirmedObjectsSource } from 'components/Map/Sources/GeoconfirmedObjectsSource';
import { GoogleLabelsSource } from 'components/Map/Sources/GoogleLabelsSource';
import { GoogleSatelliteSource } from 'components/Map/Sources/GoogleSatelliteSource';
import { ImagerySource } from 'components/Map/Sources/ImagerySource';
import { InfrastructureSource } from 'components/Map/Sources/InfrastructureSource';
import {
  LayersSource,
  useLayersSourceData,
} from 'components/Map/Sources/LayersSource';
import { MapboxSatelliteSource } from 'components/Map/Sources/MapboxSatelliteSource';
import { OpenTopoMapSource } from 'components/Map/Sources/OpenTopoMapSource';
import { RailroadsSource } from 'components/Map/Sources/RailroadsSource';
import { RailStationsSource } from 'components/Map/Sources/RailStationsSource';
import { SovietMilitaryMapSource } from 'components/Map/Sources/SovietMilitaryMapSource';
import { StaticSource } from 'components/Map/Sources/StaticSource';
import ImageryUploadModal from 'components/Map/Tabs/Imagery/ImageryUploadModal';
import {
  ScreenCaptureArea,
  ScreenCaptureAreaSize,
  ScreenCapturePoint,
} from 'components/ScreenCaptureArea';
import {
  calculateGeometryCenter,
  createCanvasScreenshot,
  getAdjustedBounds,
  getBoundsFromIDs,
  getEntityParametersTitleToIdMap,
  getFormattedDate,
  getGeoJSONCompletedFeature,
  getMapEntityChildrenIds,
  getMapObjectEntityValues,
  getQueriedFeaturesBounds,
  isMobileDevice,
  mapEntityTypeFilterFunc,
} from 'utils';
import { transformTileRequest } from 'utils/tileserver';

import { createQuickMapObject } from './utils';

interface MapProps {
  areEntitiesLoaded: boolean;
}

export const Map: FC<MapProps> = ({ areEntitiesLoaded }) => {
  const isMobile = isMobileDevice();

  const { viewport, defaultConfig } = useMap();
  const { mapRef } = useMapRef();
  const { entityId } = useParams<{
    entityId: string;
  }>();

  const { tileToken } = useAppSelector(authSelector);
  const showCustomCursors = useAppSelector(showCustomCursorsSelector);
  const contextMenu = useAppSelector(contextMenuSelector);
  const interactionMode = useAppSelector(interactionModeSelector);
  const entities = useAppSelector(entitiesSelector);
  const imagery = useAppSelector(imagerySelector);
  const boundaryGridsMap = useAppSelector(boundaryGridMapSelector);
  const mapToolsInfoPopup = useAppSelector((state) =>
    modalSelector(state, MAP_TOOLS_INFO_POPUP)
  );
  const mapObjectTemplate = useAppSelector((state) =>
    predefinedTemplateSelector(state, predefinedTemplates.MAP_OBJECT)
  );
  const mapLayerTemplate = useAppSelector((state) =>
    predefinedTemplateSelector(state, predefinedTemplates.MAP_LAYER)
  );
  const imageryUploadModal = useAppSelector((state) =>
    modalSelector(state, IMAGERY_UPLOAD_MODAL)
  );
  const entityPreviewModal = useAppSelector((state) =>
    modalSelector(state, ENTITY_PREVIEW_MODAL)
  );
  const entityDetailsModal = useAppSelector((state) =>
    modalSelector(state, ENTITY_DETAILS_MODAL)
  );
  const createFeatureModal = useAppSelector((state) =>
    modalSelector(state, CREATE_DRAW_FEATURE_MODAL)
  );

  const [isGrab, setIsGrab] = useState(false);
  const [isCursorOnFeature, setIsCursorOnFeature] = useState(false);
  const [cursor, setCursor] = useState<AstraCursorTitle | undefined>();
  const [shouldInitiateShare, setShouldInitiateShare] = useState(false);
  const [didFindChildren, setDidFindChildren] = useState(false);

  const [updatedFields, setUpdatedFields] = useState<FieldsUpdateState>({
    isRelinkedObject: false,
    isUpdatedSelectionFields: false,
    isUpdatedTextFields: false,
  });

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { handleDrawClear } = useMapDraw();
  const {
    commonTool,
    temporaryEntities,
    paramIdMap,
    isCreateQuickEntityModalOpen,
    handleEntityCreate,
    openModalWithEntity,
  } = useQuickEntityCreation();

  const layersSourceData = useLayersSourceData(
    entities,
    temporaryEntities,
    mapObjectTemplate
  );

  const isEntityPreviewModalOpen =
    entityPreviewModal?.isOpen &&
    entityPreviewModal?.props &&
    mapObjectTemplate;

  const isEntityDetailsModalOpen =
    entityDetailsModal?.isOpen &&
    entityDetailsModal?.props &&
    mapObjectTemplate;

  const entityPreviewEntity = entityPreviewModal?.props?.entity;
  const entityDetailsEntity = entityDetailsModal?.props?.entity;

  const useCloseModalIfPredicate = (modalId: string, predicate: boolean) => {
    useEffect(() => {
      if (predicate) {
        modalId === ENTITY_DETAILS_MODAL
          ? handleCloseDetailsModal()
          : handleClosePreviewModal();
      }
    }, [predicate]);
  };

  const useCloseModalIfEntityDeleted = (
    entity: Entity | undefined,
    modalId: string
  ) => {
    const isTemporaryEntities = temporaryEntities.length > 0;
    // if any modal is open and its entity no longer exists and no entity is being drawn
    const predicate = Boolean(
      (isEntityDetailsModalOpen || isEntityPreviewModalOpen) &&
        (!entity || !entities[String(entity.id)]) &&
        !isTemporaryEntities
    );
    useCloseModalIfPredicate(modalId, predicate);
  };

  const useCloseModalIfAnotherOpen = () => {
    useCloseModalIfPredicate(
      ENTITY_PREVIEW_MODAL,
      Boolean(isEntityDetailsModalOpen && isEntityPreviewModalOpen)
    );
  };

  const setViewport = useDebouncedCallback(
    () => localStorage.setItem('viewport', JSON.stringify(viewport)),
    500
  );

  const enableDblClickZoom = () => {
    mapRef.current?.doubleClickZoom.enable();
  };

  const disableDblClickZoom = () => {
    mapRef.current?.doubleClickZoom.disable();
  };

  const queryFeaturesByLayer = (
    e: MapMouseEvent | MapTouchEvent,
    layers?: string[]
  ) =>
    mapRef.current?.queryRenderedFeatures(getQueriedFeaturesBounds(e.point), {
      layers: layers?.filter((i) => !!mapRef.current?.getLayer(i)),
    });

  const queryLayers = (e: MapMouseEvent | MapTouchEvent) => {
    const pointEntityIds = Object.keys(layersSourceData.points).map(
      (key) => `${LAYERS_POINT}-${key}-main`
    );

    return queryFeaturesByLayer(e, [
      LAYERS_POLYGON_MAIN_FILL,
      LAYERS_POLYGON_MAIN_LINE,
      LAYERS_LINE_MAIN_FILL,
      ...pointEntityIds,
    ]);
  };

  const queryImagery = (
    e: MapMouseEvent | MapTouchEvent,
    layer: string,
    imagery: IImageryPreview[]
  ) => {
    const selectedFeatures = queryFeaturesByLayer(e, [layer]);

    if (
      selectedFeatures?.length &&
      selectedFeatures[0].properties &&
      selectedFeatures[0].properties['relatedFeatureId']
    ) {
      const targetId = selectedFeatures[0].properties['relatedFeatureId'];
      const targetImagery = imagery.find((i) => i.id === targetId);

      return targetImagery;
    }

    return null;
  };

  const openModalWithEntityId = (entityID: string, modalId: string) => {
    const entity = entities[entityID];
    entity && openModalWithEntity(entity, modalId);
  };

  const handleClosePreviewModal = () =>
    dispatch(modalsActions.removeModal(ENTITY_PREVIEW_MODAL));

  const handleCloseDetailsModal = () => {
    dispatch(modalsActions.removeModal(ENTITY_DETAILS_MODAL));
    dispatch(toolsStateActions.setInteractionMode(MapDrawModes.simple_select));
    dispatch(
      layersStateActions.setTemporaryEntities(
        temporaryEntities.filter((entity) => !entity.state.draft)
      )
    );

    handleDrawClear();
  };

  const handleCloseDrawFeatureCreateModal = () =>
    dispatch(modalsActions.removeModal(CREATE_DRAW_FEATURE_MODAL));

  const resetInteractionMode = useCallback(() => {
    handleDrawClear();
    dispatch(toolsStateActions.setInteractionMode(MapDrawModes.simple_select));

    if (MapCustomDrawModes[interactionMode as MapCustomDrawModes]) {
      dispatch(drawActions.setDrawFeature(null));
      dispatch(modalsActions.removeModal(MAP_TOOLS_INFO_POPUP));
    }

    if (MapCommonModes[interactionMode as MapCommonModes]) {
      dispatch(commonActions.setCommonTool(null));
    }
  }, [interactionMode, dispatch]);

  const handleDrawCreate = useCallback(
    (e: DrawCreateEvent | DrawCreateCustomEvent) => {
      const event = e as DrawCreateCustomEvent;
      const shouldCreate = event.action === DrawEventActions.DRAW_CREATE;
      const feature = event.features[0];

      if (shouldCreate && feature) {
        handleEntityCreate(feature.geometry);
      }
    },
    [handleEntityCreate]
  );

  const handleModeChange = useCallback(
    (e: DrawModeChangeCustomEvent) => {
      if (e.mode !== MapDrawModes.simple_select) {
        return;
      }

      if (
        e.action === DrawEventActions.DRAW_CANCEL ||
        e.action === DrawEventActions.DRAW_CREATE
      ) {
        resetInteractionMode();
      }
    },
    [dispatch, resetInteractionMode]
  );

  const handleDrawDragMove = useCallback(
    (e: DrawDragMoveCustomEvent) => {
      const drawFeature = e.features[0];

      dispatch(drawActions.setDrawFeature(drawFeature));
    },
    [handleEntityCreate]
  );

  const handleDrawFeatureCommonEvent = useCallback(
    (e: DrawFeatureCommonEvent) => {
      const drawFeature = e.featureTarget as DrawCustomFeature;

      dispatch(drawActions.setDrawFeature(drawFeature));
    },
    []
  );

  const handleBoundaryGridClick = (e: MapMouseEvent | MapTouchEvent) => {
    const boundaryGridCell = queryFeaturesByLayer(e, [
      BOUNDARY_GRID_CELL_LAYER,
    ])?.[0] as BoundaryGridCellGeoJSON | undefined;

    if (!boundaryGridCell) {
      return;
    }

    dispatch(
      boundaryGridActions.updateBoundaryGridCell({
        relatedFeatureId: boundaryGridCell.properties.relatedFeatureId,
        id: boundaryGridCell.id || boundaryGridCell.properties.id,
        properties: { filled: !boundaryGridCell.properties.filled },
      })
    );
  };

  const handleGeocoderFeatureClick = (e: MapMouseEvent | MapTouchEvent) => {
    const geocoderFeature = queryFeaturesByLayer(e, [
      GEOCODER_POINT_MAIN,
      GEOCODER_POLYGON_MAIN_FILL,
    ])?.[0];

    if (!geocoderFeature) {
      return;
    }

    dispatch(geocoderActions.setGeocoderResult(null));
  };

  const handleImageryClick = (e: MapMouseEvent | MapTouchEvent) => {
    const targetImagery = queryImagery(
      e,
      IMAGERY_PREVIEW_FILL_LAYER,
      imagery.previews
    );

    if (targetImagery && !imagery.pending) {
      dispatch(
        getImageryThunk({
          id_list: [targetImagery.id],
          isPreviewRequest: true,
        })
      );
    }
  };

  const queryFeaturesOpenModal = (
    e: MapMouseEvent | MapTouchEvent,
    modalId: string
  ) => {
    const queriedFeatures = queryLayers(e);

    if (
      queriedFeatures &&
      queriedFeatures?.length > 0 &&
      queriedFeatures[0].id
    ) {
      disableDblClickZoom();
      openModalWithEntityId(String(queriedFeatures[0].id), modalId);
    }
  };

  const handleEntityClick = (e: MapMouseEvent | MapTouchEvent) =>
    !entityDetailsModal?.isOpen &&
    queryFeaturesOpenModal(e, ENTITY_PREVIEW_MODAL);

  const handlePointEntitiesClusterClick = (
    e: MapMouseEvent | MapTouchEvent
  ) => {
    const pointEntityClusterIds = Object.keys(layersSourceData.points).reduce(
      (acc, curr) => [
        ...acc,
        `${LAYERS_POINT}-${curr}-cluster`,
        `${LAYERS_POINT}-${curr}-cluster-counter`,
      ],
      [] as string[]
    );

    const cluster = queryFeaturesByLayer(
      e,
      pointEntityClusterIds
    )?.[0] as PointClusterGeoJSON;

    if (!cluster) {
      return;
    }

    const source = mapRef.current?.getSource(cluster.source ?? '') as
      | GeoJSONSource
      | undefined;

    source?.getClusterExpansionZoom(
      cluster.properties.cluster_id,
      (error, zoom) => {
        const center = cluster?.geometry?.coordinates as [number, number];

        if (error) {
          return console.log(error);
        }

        mapRef.current?.easeTo({
          center: center,
          zoom: zoom ?? 0,
        });
      }
    );
  };

  const handleMapDoubleClick = (e: MapMouseEvent | MapTouchEvent) => {
    enableDblClickZoom();
    handleClosePreviewModal();
    if (isMobile && contextMenu) {
      disableDblClickZoom();
      handleContextMenuClose();
    }
    queryFeaturesOpenModal(e, ENTITY_DETAILS_MODAL);
  };

  const handleArtilleryMeasure = (
    position: number[],
    interactionMode: string
  ) => {
    if (interactionMode === MapCommonModes.measure_artillery) {
      dispatch(
        commonActions.updateCommonTool({
          position,
        })
      );
    }
  };

  const handleQuickEntityCreate = async (
    position: number[],
    interactionMode: string
  ) => {
    if (
      interactionMode !== MapCommonModes.create_quick_entity ||
      commonTool?.type !== MapCommonModes.create_quick_entity ||
      !mapObjectTemplate ||
      !mapLayerTemplate
    ) {
      return;
    }

    const quickEntity = createQuickMapObject(
      mapObjectTemplate,
      mapLayerTemplate,
      paramIdMap,
      position,
      commonTool.value.type,
      commonTool.value.primalParentId ?? 0
    ).entity;

    const quickEntityParams = {
      ...quickEntity,
      ...(commonTool.value.secondaryParentName && {
        autoFolder: {
          title: commonTool.value.secondaryParentName,
          templateID: mapLayerTemplate.template.id,
        },
      }),
    };

    if (commonTool.value.primalParentId) {
      dispatch(createQuickMapEntityThunk(quickEntityParams));
    } else {
      dispatch(commonActions.updateCommonTool({ entity: quickEntity }));
      dispatch(
        modalsActions.addModal({
          id: CREATE_QUICK_ENTITY_MODAL,
          isOpen: true,
        })
      );
    }
  };

  const withMapInteractionModeEnabledClick = async (
    e: MapMouseEvent | MapTouchEvent,
    interactionMode: string
  ) => {
    const position = [e.lngLat.lng, e.lngLat.lat];

    handleArtilleryMeasure(position, interactionMode);
    handleQuickEntityCreate(position, interactionMode);
  };

  const withInteractionModeDisabledClick = (
    e: MapMouseEvent | MapTouchEvent
  ) => {
    handleBoundaryGridClick(e);
    handleGeocoderFeatureClick(e);
    handleImageryClick(e);
    handleEntityClick(e);
    handlePointEntitiesClusterClick(e);
    handleGeocoderMarkerClick(e);
  };

  const handleGeocoderMarkerClick = (event: MapMouseEvent | MapTouchEvent) => {
    const geocoderFeature = queryFeaturesByLayer(event, [
      GEOCODER_POINT_MAIN,
    ])?.[0];

    if (geocoderFeature) {
      handleEntityCreate(geocoderFeature.geometry);
    }
  };

  const handleMapSingleClick = (e: MapMouseEvent | MapTouchEvent) =>
    interactionMode && interactionMode !== MapDrawModes.simple_select
      ? withMapInteractionModeEnabledClick(e, interactionMode)
      : withInteractionModeDisabledClick(e);

  const handleMapClickDebounced = useDebouncedCallback(
    (e: MapMouseEvent | MapTouchEvent) => {
      if (e.originalEvent.detail === 1) {
        handleMapSingleClick(e);
      } else {
        handleMapDoubleClick(e);
      }
    },
    250
  );

  const handleMapClick = (e: MapMouseEvent | MapTouchEvent) => {
    e.preventDefault();
    isMobile ? handleMapTouch(e) : handleMapClickDebounced(e);
  };

  const handleMapTouch = useDoubleTap(
    handleMapSingleClick,
    handleMapDoubleClick
  );

  const getCursorType = () => {
    // TODO: Map doesn't instantly render proper cursor, despite memo returning proper if else branch
    if (showCustomCursors && mapRef.current) {
      if (isGrab) {
        return AstraCursorTitle.GRABBING;
      } else if (interactionMode.startsWith(MAP_CREATE_MODE_PREFIX)) {
        return AstraCursorTitle.COPY;
      } else if (isCursorOnFeature) {
        return AstraCursorTitle.POINTER;
      } else {
        return AstraCursorTitle.CROSSHAIR;
      }
    } else {
      mapRef.current && (mapRef.current.getCanvas().style.cursor = '');
      return undefined;
    }
  };

  const getMapContextMenu = (
    e: MapMouseEvent | MapTouchEvent
  ): IContextMenu => {
    const { lngLat } = e;

    return {
      type: ContextMenuTypes.DEFAULT,
      latitude: lngLat.lat,
      longitude: lngLat.lng,
      props: {},
    };
  };

  const getBoundaryGridContextMenu = (
    e: MapMouseEvent | MapTouchEvent
  ): IContextMenu | null => {
    const boundaryGridCell = queryFeaturesByLayer(e, [
      BOUNDARY_GRID_CELL_LAYER,
    ])?.[0] as BoundaryGridCellGeoJSON | undefined;

    if (!boundaryGridCell) {
      return null;
    }

    const boundaryGrid =
      boundaryGridsMap[boundaryGridCell.properties.relatedFeatureId];

    const { lngLat } = e;

    return {
      type: ContextMenuTypes.BOUNDARY_GRID,
      latitude: lngLat.lat,
      longitude: lngLat.lng,
      relatedFeatureId: boundaryGridCell.properties.relatedFeatureId,
      props: { boundaryGrid, boundaryGridCell },
    };
  };

  const getImageryContextMenu = (
    e: MapMouseEvent | MapTouchEvent
  ): IContextMenu | null => {
    const imageryBoundary = queryFeaturesByLayer(e, [
      IMAGERY_BOUNDARY_LAYER,
    ])?.[0] as ImageryBoundaryGeoJSON | undefined;

    const { lngLat } = e;

    if (!imageryBoundary) {
      return null;
    }

    const completeImageryBoundary = getGeoJSONCompletedFeature(imageryBoundary);

    return {
      type: ContextMenuTypes.IMAGERY,
      latitude: lngLat.lat,
      longitude: lngLat.lng,
      relatedFeatureId: completeImageryBoundary?.properties?.relatedFeatureId,
      props: { imageryBoundary: completeImageryBoundary },
    };
  };

  const handleContextMenuOpen = (e: MapMouseEvent | MapTouchEvent) => {
    const isCreateMode =
      !!MapCustomDrawModes[interactionMode as MapCustomDrawModes];

    if (isCreateMode) {
      dispatch(
        modalsActions.addModal({ id: CREATE_DRAW_FEATURE_MODAL, isOpen: true })
      );

      return;
    }

    const boundaryGridContextMenu = getBoundaryGridContextMenu(e);
    const imageryContextMenu = getImageryContextMenu(e);
    const mapContexMenu = getMapContextMenu(e);

    const contextMenu =
      boundaryGridContextMenu || imageryContextMenu || mapContexMenu;

    dispatch(contextMenuActions.setContextMenu(contextMenu));
  };

  const handleContextMenuClose = () =>
    dispatch(contextMenuActions.setContextMenu(null));

  const handleDragStart = () => setIsGrab(true);
  const handleDragEnd = () => setIsGrab(false);

  const handleMouseMove = (e: MapMouseEvent) => {
    const features = queryLayers(e);

    features && features.length > 0
      ? setIsCursorOnFeature(true)
      : setIsCursorOnFeature(false);
  };

  useCloseModalIfAnotherOpen();
  useCloseModalIfEntityDeleted(entityDetailsEntity, ENTITY_DETAILS_MODAL);
  useCloseModalIfEntityDeleted(entityPreviewEntity, ENTITY_PREVIEW_MODAL);

  useEffect(() => {
    setViewport();
  }, [viewport]);

  useEffect(() => {
    if (
      entityId &&
      entities[entityId] &&
      !didFindChildren &&
      areEntitiesLoaded
    ) {
      setDidFindChildren(true);
      setShouldInitiateShare(true);
      dispatch(layersStateActions.setSharedEntityId(entityId));
    }
  }, [entityId, entities, areEntitiesLoaded]);

  useEffect(() => {
    if (
      entityId &&
      entities &&
      entities[entityId] &&
      mapObjectTemplate?.template.id &&
      mapLayerTemplate?.template.id &&
      shouldInitiateShare
    ) {
      setShouldInitiateShare(false);
      const entity = entities[entityId];
      const isFolder =
        entity.entity.templateID === mapLayerTemplate.template.id;
      const parametersIdTitleMap = getEntityParametersTitleToIdMap(
        mapObjectTemplate?.parameters || [],
        MAP_ENTITY_PARAM_VALUES
      );

      if (!isFolder) {
        const entityParams = getMapObjectEntityValues(
          parametersIdTitleMap,
          entity.entity
        );

        const geometryCenter = entityParams.geometry
          ? calculateGeometryCenter(entityParams.geometry)
          : undefined;

        mapRef.current?.flyTo({
          ...DEFAULT_FLY_TO_SETTINGS,
          zoom: 17,
          center: geometryCenter
            ? [geometryCenter[0], geometryCenter[1] + OFFSET_TO_FIT_PREVIEW]
            : undefined,
        });

        openModalWithEntityId(entityId, ENTITY_PREVIEW_MODAL);
      } else {
        const childrenObjects = getMapEntityChildrenIds(
          entities,
          Number(entityId),
          mapEntityTypeFilterFunc(MapEntityTypes.OBJECT)
        );
        const initialBounds = getBoundsFromIDs(
          entities,
          parametersIdTitleMap,
          childrenObjects
        );

        const adjustedBounds = getAdjustedBounds(initialBounds, mapRef.current);

        mapRef.current?.fitBounds(adjustedBounds);
      }

      setTimeout(() => navigate(mapRoutes.LAYERS), 1000);
    }
  }, [shouldInitiateShare]);

  useEffect(() => {
    const cursorToSet = getCursorType();

    setCursor(cursorToSet ?? undefined);
  }, [getCursorType, cursor]);

  if (!MAP_BOX_TOKEN) {
    return <p>Не удалось загрузить карту. Отсутствует токен</p>;
  }

  const handleScreenCaptureEnd = async (
    screenCaptureEndPoint: ScreenCapturePoint,
    screenCaptureArea: ScreenCaptureAreaSize
  ) => {
    const mapCanvas = mapRef.current?.getCanvas();
    const { x, y } = screenCaptureEndPoint;
    const { width, height } = screenCaptureArea;
    // map canvas contex size params depend on pixelRatio
    const pixelRatio = window.devicePixelRatio;

    if (!mapCanvas) {
      return;
    }

    await createCanvasScreenshot(
      mapCanvas,
      `Map_screenshot_${getFormattedDate(new Date(), 'DD.MM.YY_HH:mm', true)}`,
      x,
      y,
      width,
      height,
      pixelRatio
    );

    dispatch(toolsStateActions.setInteractionMode(MapDrawModes.simple_select));
  };

  useLoadEntityTypeImages(mapObjectTemplate, mapRef.current);

  return (
    <div
      className={cn('w-full h-full relative', {
        'map-container__wrapper__svg-pointer': showCustomCursors,
      })}
      // Sets cursor to random number for cursor enum, this is used to
      // to fire rerender of Map.tsx and apply the cursor properly
      onMouseEnter={() => setCursor(Math.random() % 7)}
    >
      <ScreenCaptureArea
        isCapture={interactionMode === MapCommonModes.capture_screenshot}
        onCaptureEnd={handleScreenCaptureEnd}
      >
        <MapBox
          {...defaultConfig}
          {...viewport}
          ref={(ref) => (mapRef.current = ref && ref.getMap())}
          projection={{ name: 'mercator' }}
          terrain={{ source: 'mapbox-dem', exaggeration: 2 }}
          cursor={cursor ? AstraCursors[cursor] : undefined}
          onContextMenu={handleContextMenuOpen}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onClick={handleMapClick}
          onDblClick={handleMapClick}
          onTouchEnd={handleMapClick}
          onMouseMove={handleMouseMove}
          transformRequest={(url, type) =>
            transformTileRequest(url, type, tileToken)
          }
        >
          <MapDrawControl
            mode={interactionMode}
            onCreate={handleDrawCreate}
            onStop={handleDrawFeatureCommonEvent}
            onModeChange={handleModeChange}
            onDragVertex={handleDrawFeatureCommonEvent}
            onDragFeature={handleDrawFeatureCommonEvent}
            onDragMove={handleDrawDragMove}
            onMouseMove={handleDrawFeatureCommonEvent}
            onClick={handleDrawFeatureCommonEvent}
            onTap={handleDrawFeatureCommonEvent}
          />
          <GoogleLabelsSource beforeId={SATELLITE_STATIC_LAYER} />
          <StaticSource id={SATELLITE_STATIC_LAYER} />
          <StaticSource id={SATELLITE_IMAGE_STATIC_LAYER} />
          <StaticSource id={ELEVATION_STATIC_LAYER} />
          <StaticSource id={LABEL_STATIC_LAYER} />
          <BoundaryGridSource />
          <StaticSource id={FRONTLINE_STATIC_LAYER} />
          <StaticSource id={POLYGON_STATIC_LAYER} />
          <StaticSource id={LOGISTIC_STATIC_LAYER} />
          <StaticSource id={LINE_STATIC_LAYER} />
          <StaticSource id={FIRE_STATIC_LAYER} />
          <StaticSource id={POINT_STATIC_LAYER} />
          <OpenTopoMapSource />
          <SovietMilitaryMapSource />
          <GoogleSatelliteSource beforeId={SATELLITE_STATIC_LAYER} />
          <MapboxSatelliteSource />
          <ImagerySource />
          <ElevationSource />
          <DemSource />
          <GeoconfirmedObjectsSource />
          <LayersSource {...layersSourceData} />
          <FeatureCollection />
          <InfrastructureSource />
          <RailroadsSource />
          <RailStationsSource />
          <FireSource />
          <GeocoderSource />
          <ArtillerySource />
          <AimSource onMapContextMenuOpen={handleContextMenuOpen} />
          <Legend />
          {contextMenu && (
            <ContextMenu
              contextMenu={contextMenu}
              onClose={handleContextMenuClose}
              onEntityCreate={handleEntityCreate}
            />
          )}
          {mapToolsInfoPopup?.isOpen && <MapToolsInfoPopup />}
          {imageryUploadModal?.isOpen && <ImageryUploadModal />}
          {isEntityPreviewModalOpen && (
            <EntityPreview
              key={entityPreviewEntity?.id}
              entity={entityPreviewModal.props as MapEntity}
              mapObjectTemplate={mapObjectTemplate}
              onClose={handleClosePreviewModal}
            />
          )}
          {isEntityDetailsModalOpen && (
            <EntityDetails
              key={
                entityDetailsEntity
                  ? entityDetailsEntity.id
                  : new Date().getTime()
              }
              entity={entityDetailsModal.props as MapEntity}
              mapObjectTemplate={mapObjectTemplate}
              updatedFields={updatedFields}
              setUpdatedFields={setUpdatedFields}
              onClose={handleCloseDetailsModal}
            />
          )}
          {isCreateQuickEntityModalOpen && <CreateQuickEntityModal />}
          {createFeatureModal?.isOpen && (
            <CreateDrawFeatureModal
              key={interactionMode}
              onClose={handleCloseDrawFeatureCreateModal}
            />
          )}
        </MapBox>
      </ScreenCaptureArea>
    </div>
  );
};
